import React from "react"
import styled from "styled-components"
import PropTypes from "prop-types"
import Slider from "react-slick"
import AniLink from "gatsby-plugin-transition-link/AniLink"
import { AnchorLink } from "gatsby-plugin-anchor-links"
import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import BackgroundImage from "gatsby-background-image"
import { useInstaPosts } from "../../hooks/useInstaPosts"
import fb from "../../images/icons/footer-socials/fb.svg"
import insta from "../../images/icons/footer-socials/insta.svg"
import be from "../../images/icons/footer-socials/be.svg"
import linked from "../../images/icons/footer-socials/linked.svg"
import dribbble from "../../images/icons/footer-socials/dribbble.svg"
import policyprivacy from "../../docs/polityka.pdf"

const StyledFooter = styled.footer`
  background-color: #000;
  padding: 0 16px 36px;
  position: relative;
  margin-top: ${props => (props.hideInstaPosts ? "0" : "170px")};
  .slick-track {
    display: flex;
    margin-left: 0;
    margin-right: 0;
    .slick-slide {
      width: 242px;
      margin-right: 16px;
    }
  }
  .slick-slider {
    width: 100vw;
    margin-left: 0;
    transform: translateY(-50%);
    display: ${props => (props.hideInstaPosts ? "none" : "block")};
    @media (min-width: 1600px) {
      max-width: 1200px;
      margin: 0 auto;
    }
  }
  @media (min-width: 1024px) {
    padding: 0 16px 36px;
  }
`

const StyledSubList = styled.ul`
  display: flex;
  flex-direction: column;
  justify-content: center;
  @media (min-width: 1024px) {
    justify-content: flex-start;
  }
`

const StyledList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  margin-top: 36px;
  li {
    font-size: 16px;
    color: #fff;
    letter-spacing: 0.3px;
    padding: 12px 0;
    text-align: center;
    opacity: 0.75;
    & a {
      color: inherit;
      text-decoration: none;
    }
  }
  @media (min-width: 1024px) {
    max-width: 1200px;
    li {
      font-size: 14px;
      text-align: left;
      padding: 15px 0;
    }
    & .sublists {
      display: flex;
      justify-content: space-between;
      ${StyledSubList}:last-child {
        margin-right: 25px;
      }
    }
  }
  @media (min-width: 1200px) {
    li {
      font-size: 16px;
    }
  }
`

const Box = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: -100px;
  ${StyledList}:first-child {
    li {
      color: #ffffff;
      opacity: 0.75;
      & a {
        color: #7379ee;
        text-decoration: none;
      }
    }
  }
  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr 1fr 2fr;
    max-width: 900px;
    grid-column-gap: 130px;
    margin: 0 auto;
  }
  @media (min-width: 1200px) {
    max-width: 1200px;
  }
`

const TitleLi = styled.li`
  font-family: "Made Bold", sans-serif;
  color: #fff !important;
  font-size: 14px;
  margin: 0 auto;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 156px;
    height: 1px;
    background-color: #c39779;
    transform: translateX(-50%);
  }
  @media (min-width: 1024px) {
    margin: 0;
    &::after {
      width: 100%;
    }
  }
`

const Copy = styled.div`
  margin-top: 36px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-top: 1px solid #979797;
  & p,
  a {
    color: #808080;
    margin: 12px 0;
    text-decoration: none;
    letter-spacing: 0.3px;
    text-align: center;
  }
  @media (min-width: 1024px) {
    margin: 18px auto 0;
    max-width: 1200px;
    & p,
    a {
      margin: 15px 0;
    }
  }
`

const InstaBox = styled(BackgroundImage)`
  width: 242px;
  height: 242px;
  overflow: hidden;
  background-color: #f9d6c1;
  position: relative;
  border-radius: 8px;
  & h3 {
    width: 100%;
    height: 100%;
    background-color: #000;
    opacity: 0;
    margin: 0;
    transition: 0.2s ease;
    color: #fff;
  }
  &:hover {
    & h3 {
      opacity: 0.7;
    }
  }
`

const Socials = styled.div`
  display: flex;
  margin-top: 8px;
  align-items: center;
  justify-content: center;
  & a {
    margin-right: 30px;
    & img {
      transition: all 0.15s ease;
    }
    &:hover {
      img {
        transform: scale(1.1);
      }
    }
    &:last-child {
      margin-right: 0;
    }
  }
`

const Footer = ({ hideInstaPosts }) => {
  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
    arrows: false,
  }
  const renderPosts = () => {
    const { instaPosts } = useInstaPosts()
    const list = instaPosts.edges.map(({ node: post }) => {
      const { caption, localFile } = post
      return (
        <InstaBox
          Tag="div"
          fluid={localFile.childImageSharp.fluid}
          key={caption}
        />
      )
    })
    return list
  }
  return (
    <StyledFooter hideInstaPosts={hideInstaPosts}>
      <Slider {...settings}>{renderPosts()}</Slider>
      <Box>
        <StyledList>
          <TitleLi>KONTAKT</TitleLi>
          <li>
            <a href="mailto:kontakt@nismedia.pl">kontakt@nismedia.pl</a>
          </li>
          <li>
            Katowice: <a href="tel:500 211 637">500 211 637</a>
          </li>
          <li>
            Dzierżoniów: <a href="tel:663 901 891">663 901 891</a>
          </li>
        </StyledList>
        <StyledList>
          <TitleLi>MAPA WITRYNY</TitleLi>
          <li>
            <AniLink cover to="/" bg="#474dc4">
              Strona główna
            </AniLink>
          </li>
          <li>
            <AnchorLink to="/#realizacje">Relizacje</AnchorLink>
          </li>
          <li>
            <AniLink cover to="/o-nas/" bg="#474dc4">
              O nas
            </AniLink>
          </li>
          <li>
            <AniLink cover to="/blog/" bg="#474dc4">
              Blog
            </AniLink>
          </li>
        </StyledList>
        <StyledList>
          <TitleLi>OFERTA</TitleLi>
          <li className="sublists">
            <StyledSubList>
              <li>
                <AniLink cover to="/aplikacje-internetowe/" bg="#474dc4">
                  Aplikacje Internetowe
                </AniLink>
              </li>
              <li>
                <AniLink cover to="/sklepy-internetowe/" bg="#474dc4">
                  Sklepy Internetowe
                </AniLink>
              </li>
              <li>
                <AniLink cover to="/strony-internetowe/" bg="#474dc4">
                  Strony Internetowe
                </AniLink>
              </li>
              <li>
                <AniLink cover to="/ux-ui-design/" bg="#474dc4">
                  UI/UX Design
                </AniLink>
              </li>
              <li>
                <AniLink cover to="/badania-rynku/" bg="#474dc4">
                  Badania Rynku
                </AniLink>
              </li>
            </StyledSubList>
            <StyledSubList>
              <li>
                <AniLink cover to="/materialy-drukowane/" bg="#474dc4">
                  Materiały Drukowane
                </AniLink>
              </li>
              <li>
                <AniLink cover to="/projektowanie-logo/" bg="#474dc4">
                  Projektowanie Logo
                </AniLink>
              </li>
              <li>
                <AniLink cover to="/neuromarketing/" bg="#474dc4">
                  Neuromarketing
                </AniLink>
              </li>
              <li>
                <AniLink cover to="/social-media/" bg="#474dc4">
                  Social Media
                </AniLink>
              </li>
              <li>
                <AniLink cover to="/marketing/" bg="#474dc4">
                  Marketing
                </AniLink>
              </li>
            </StyledSubList>
          </li>
        </StyledList>
      </Box>
      <Copy>
        <Socials>
          <a
            href="https://www.facebook.com/NisMediaReklama/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={fb} alt="Facebook - Nis Media" title="Facebook" />
          </a>
          <a href="/" target="_blank" rel="noopener noreferrer">
            <img src={be} alt="Behance - Nis Media" title="Behance" />
          </a>
          <a href="/" target="_blank" rel="noopener noreferrer">
            <img src={dribbble} alt="Dribbble - Nis Media" title="Dribbble" />
          </a>
          <a
            href="https://www.linkedin.com/company/nismedia/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={linked} alt="LinkedIn - Nis Media" title="LinkedIn" />
          </a>
          <a
            href="https://www.instagram.com/nis_media/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={insta} alt="Instagram - Nis Media" title="Instagram" />
          </a>
        </Socials>
        <p>Cypyright © 2020 Nis Media. All rights reserved.</p>
        <a href={policyprivacy} target="_blank" rel="noopener noreferrer">
          Polityka Prywatności
        </a>
      </Copy>
    </StyledFooter>
  )
}

export default Footer

Footer.propTypes = {
  hideInstaPosts: PropTypes.bool,
}

Footer.defaultProps = {
  hideInstaPosts: false,
}
